import { ReactNode, useEffect, useRef } from 'react';

import NoRecordIcon from '../Icons/NoRecordIcon';
import PaginationComponent from './PaginationComponent';
import Button from '../ui/Button';
import {
  employeeStatusHandler,
  getInitials,
  handleAssetRequestStatus,
  handleAssetStatus,
  handleAttendanceStatus,
  handleSalaryStatus,
  handleStatus,
} from '@/utility/CommonFunctions';
import { capitalizeFirstLetter } from '@/utility/helper';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import CardView from './CardView';
import { useDispatch } from 'react-redux';
import { setUpdateFilterStates } from '@/redux/features/UserSlice';
import Sanitized from './Sanitized';
import moment from 'moment';
import { ITableRowData } from '@/interfaces/common.model';
import StatusIcon from '../Icons/StatusIcon';

export interface ITableColumn<T> {
  value: keyof T;
  label: string | ReactNode;
}
export interface IActionData<T extends ITableRowData> {
  label: string | ReactNode;
  className: string;
  icon: ReactNode;
  title: string;
  clickHandler: (data: T) => void;
  disabled?: boolean | ((data: T) => boolean);
  isHidden?: (data: T) => boolean;
}
interface ICommonTableProps<T extends ITableRowData> {
  tableData: T[];
  onSelectionChange?: () => void;
  count: number;
  pageSize: number;
  isLoading?: boolean;
  tableHeading: ITableColumn<T>[];
  currentPage: number;
  ActionData?: IActionData<T>[];
  totalPages: number;
  handleViewDetail?: (data: T) => void;
  pageChangeHandler?: (PageNo: number) => void;
  handleStatusToggle?: (
    data: T,
    status: boolean,
    heading?: string | number,
  ) => void;
  handleChangePageSize?: (value: number) => void;
  handleChangeAttendance?: (
    newStatus: number,
    userId: string,
    userName: string,
  ) => void;
  handleChangeTime?: (
    id: string,
    time: string,
    field: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  selectedIds?: string[];
  handleSelection?: (id: string) => void;
  isSelectionDisabled?: (data: T) => boolean;
  handleApprove?: (data?: T) => void;
  handleReject?: (data?: T) => void;
}

const pageSizeMap = [
  { grid: 9, table: 10 },
  { grid: 18, table: 20 },
  { grid: 27, table: 30 },
  { grid: 36, table: 40 },
  { grid: 45, table: 50 },
  { grid: 54, table: 100 },
];

const gridToTable = (gridSize: number) => {
  return pageSizeMap.find((p) => p.grid === gridSize)?.table ?? 10;
};
const tableToGrid = (tableSize: number) => {
  return pageSizeMap.find((p) => p.table === tableSize)?.grid ?? 9;
};

export default function CommonTable<T extends ITableRowData>({
  ActionData,
  onSelectionChange,
  tableHeading,
  isLoading,
  tableData,
  count,
  pageSize,
  currentPage,
  totalPages,
  pageChangeHandler,
  handleStatusToggle,
  handleChangePageSize,
  handleChangeAttendance,
  handleViewDetail,
  handleChangeTime,
  selectedIds,
  handleSelection,
  isSelectionDisabled,
  handleApprove,
  handleReject,
}: ICommonTableProps<T>) {
  const userDetails = useSelector((state: RootState) => state?.user?.user);
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const filtersStates = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const pageSizes =
    tableView === 'table' ? [10, 20, 30, 40, 50, 100] : [9, 18, 27, 36, 45, 54];
  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    el.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        el.scrollLeft += 120;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        el.scrollLeft -= 120;
      }
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, []);
  useEffect(() => {
    if (!filtersStates?.pageSize) return;

    if (tableView === 'grid') {
      const isValidGridSize = [9, 18, 27, 36, 45, 54].includes(
        filtersStates.pageSize,
      );
      if (!isValidGridSize) {
        const gridSize = tableToGrid(filtersStates.pageSize);
        dispatch(setUpdateFilterStates({ pageSize: gridSize }));
      }
    } else {
      const isValidTableSize = [10, 20, 30, 40, 50, 100].includes(
        filtersStates.pageSize,
      );
      if (!isValidTableSize) {
        const tableSize = gridToTable(filtersStates.pageSize);
        dispatch(setUpdateFilterStates({ pageSize: tableSize }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableView, dispatch]);
  const renderTableData = (
    heading: ITableColumn<T>,
    data: T,
    index: number,
  ) => {
    switch (heading?.value) {
      case 'selectAll':
        return (
          <td className="text-start">
            <input
              className="form-check-input"
              type="checkbox"
              checked={selectedIds?.includes(data?.id as string)}
              disabled={isSelectionDisabled && isSelectionDisabled(data)}
              onChange={() =>
                handleSelection && handleSelection(data?.id as string)
              }
            />
          </td>
        );
      case 'index':
        return (
          <td className="text-start">
            {((currentPage - 1) * pageSize + index + 1)
              .toString()
              .padStart(2, '0')}
          </td>
        );
      case 'canAccessTeam':
      case 'toggleStatus':
        return (
          <td className="status">
            <div className="form-check form-switch d-flex justify-content-center">
              <input
                type="checkbox"
                className="form-check-input rounded-4 "
                checked={!!data[heading.value]}
                onChange={
                  handleStatusToggle
                    ? () =>
                        handleStatusToggle(
                          data,
                          Boolean(data[heading.value]),
                          heading?.value as string | number,
                        )
                    : () => {}
                }
              />
            </div>
          </td>
        );
      case 'companylogo':
        return (
          <td>
            <div className="me-5">
              <span>
                {data[heading?.value] ? (
                  <img
                    src={String(data[heading?.value]).trim()}
                    alt=""
                    className="rounded"
                    width="40"
                    height="40"
                    loading="lazy"
                    style={{ objectFit: 'contain' }}
                  />
                ) : (
                  <img
                    src={'public/company.jpeg'}
                    alt=""
                    className="rounded"
                    width="40"
                    height="40"
                    loading="lazy"
                    style={{ objectFit: 'contain' }}
                  />
                )}
              </span>
            </div>
          </td>
        );
      case 'details':
        return (
          <td className="text-start">
            <div className="table_text">
              <p className="mb-1">
                <span className="fw-semibold">Category : </span>
                {data?.assetCategory}
              </p>
              <p className="mb-1">
                <span className="fw-semibold">Description : </span>
                {data?.description}
              </p>
              {data?.createdAt && (
                <p className="mb-1">
                  <span className="fw-semibold">Requested On : </span>
                  {moment(data.createdAt).format('DD MMM YYYY')}
                </p>
              )}
              {data.isAllocate && (
                <p className="mb-1">
                  <span className="fw-semibold">Requested On : </span>
                  {data.receivedAt}
                </p>
              )}
              {data.isTransfer && (
                <p className="mb-1">
                  <span className="fw-semibold">Transfer On : </span>
                  {moment(data.receivedAt).format('DD MMM YYYY')}
                </p>
              )}
              {data.isReturn && (
                <p className="mb-1">
                  <span className="fw-semibold">Return On : </span>
                  {moment(data.updatedAt).format('DD MMM YYYY')}
                </p>
              )}
            </div>
          </td>
        );
      case 'asset':
        return (
          <td>
            <div className="text-start fs-5 fw">
              <span>{String(data[heading?.value])}</span>
            </div>
          </td>
        );
      case 'leaveStatus':
      case 'status':
        return handleStatus(Number(data[heading.value]));
      case 'assetStatus':
        return handleAssetStatus(Number(data[heading.value]));
      case 'attendanceStatus':
        return handleAttendanceStatus(Number(data[heading?.value]), data);
      case 'AttendanceLockStatus': {
        if (data[heading.value] === true) {
          return (
            <td className="text-start">
              <span className="badge  bg-green-25 fw-normal rounded-pill">
                Yes
              </span>
            </td>
          );
        } else {
          return (
            <td className="text-start">
              <span className="badge bg-red-25 fw-normal rounded-pill">No</span>
            </td>
          );
        }
      }
      case 'assetRequestStatus': {
        return handleAssetRequestStatus(Number(data[heading.value]));
      }
      case 'salaryStatus': {
        if (data[heading.value] === true) {
          return (
            <td className="text-start">
              <span className="badge  bg-green-25 fw-normal rounded-pill">
                Yes
              </span>
            </td>
          );
        } else {
          return (
            <td className="text-start">
              <span className="badge bg-red-25 fw-normal rounded-pill">No</span>
            </td>
          );
        }
      }
      case 'content':
        return (
          <td className="text-start">
            <Sanitized description={(data[heading?.value] as string) || ''} />
          </td>
        );
      case 'empNameWithImage': {
        const emp = String(data[heading.value] ?? '');
        const [name, image] = emp.split('##');
        return (
          <td className="fixed-column">
            <div
              className="user d-flex align-items-center"
              style={{ cursor: handleViewDetail ? 'pointer' : 'text' }}
            >
              {image != 'null' ? (
                <img
                  src={image}
                  alt="User"
                  loading="lazy"
                  className="user-avatar me-2"
                  style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <span className="user-icon me-2">{getInitials(name)}</span>
              )}

              <div
                onClick={
                  handleViewDetail ? () => handleViewDetail(data) : () => {}
                }
              >
                <h4 className="title mb-0">{capitalizeFirstLetter(name)}</h4>
              </div>
            </div>
          </td>
        );
      }

      case 'salary':
        return handleSalaryStatus(data[heading.value] as boolean);
      case 'employeeStatus':
        return employeeStatusHandler(data[heading.value] as string);
      case 'attendanceStatusToggle': {
        const getClassName = () => {
          switch (Number(data[heading.value])) {
            case 1:
              return 'text-success';
            case 0:
              return 'text-danger';
            case 2:
              return 'text-warning';
            case 3:
              return 'text-info';
            case 4:
              return 'text-warning';
            case 5:
              return 'text-warning';
            case 6:
              return 'text-warning';
            case 7:
              return 'text-danger';
            case 8:
              return 'text-danger';
            case 9:
              return 'text-primary';
            case 10:
              return 'text-danger';
            default:
              return '';
          }
        };
        return (
          <td>
            <select
              className={`form-select fw-normal status-dropdown rounded-4 px-3 ${getClassName()}`}
              defaultValue={String(data[heading.value])}
              value={String(data[heading.value])}
              onChange={(e) =>
                handleChangeAttendance &&
                handleChangeAttendance(
                  Number(e.target.value),
                  data?.id,
                  data?.name ?? '',
                )
              }
              disabled={
                userDetails?.department?.name !== 'Management' ||
                data[heading.value] === 4 ||
                data[heading.value] === 5 ||
                data[heading.value] === 6 ||
                data[heading.value] === 7
              }
            >
              {data?.isHoliday && (
                <option style={{ color: 'green' }} value={7}>
                  Holiday
                </option>
              )}
              <option style={{ color: 'green' }} value={1}>
                Present
              </option>

              <option style={{ color: 'red' }} value={0}>
                Absent
              </option>
              <option style={{ color: 'orange', display: 'none' }} value={2}>
                Pending
              </option>

              <option style={{ color: 'cyan' }} value={3}>
                On Leave
              </option>
              <option style={{ color: 'orange', display: 'none' }} value={4}>
                Pending Leave
              </option>

              <option style={{ color: 'orange', display: 'none' }} value={5}>
                Pending AR
              </option>

              <option style={{ color: 'orange', display: 'none' }} value={6}>
                Pending WFH
              </option>
              <option style={{ color: 'red', display: 'none' }} value={7}>
                Weekly Off
              </option>
              <option style={{ color: 'red', display: 'none' }} value={8}>
                Half Day Leave
              </option>
              <option style={{ color: 'blue', display: 'none' }} value={9}>
                WFH
              </option>
              <option style={{ color: 'red', display: 'none' }} value={10}>
                Cancel AR
              </option>
            </select>
          </td>
        );
      }

      case 'checkIn':
      case 'checkOut': {
        return (
          <td className="text-start">
            <input
              type="time"
              className="form-control"
              value={String(data[heading.value])}
              disabled={
                (heading.value === 'checkIn' && !!data.isCheckInDisabled) ||
                (heading.value === 'checkOut' && !data['checkIn']) ||
                [4, 5, 6, 7].includes(Number(data.attendanceStatusToggle))
              }
              onChange={(e) =>
                handleChangeTime &&
                handleChangeTime(
                  data.id,
                  e.target.value,
                  String(heading.value),
                  e,
                )
              }
            />
          </td>
        );
      }
      case 'requestAction': {
        return (
          <td className="text-center">
            {Number(data?.leaveStatus || data?.status) === 0 && (
              <div className="d-flex gap-2 justify-content-center">
                <button
                  className="btn btn-success-light p-1 border-0"
                  onClick={() => handleApprove && handleApprove(data)}
                  title="Approve"
                >
                  <StatusIcon status="approved" />
                </button>
                <button
                  className="btn btn-danger-light p-1 border-0"
                  onClick={() => handleReject && handleReject(data)}
                  title="Reject"
                >
                  <StatusIcon status="rejected" />
                </button>
              </div>
            )}
          </td>
        );
      }
      default:
        return (
          <td className="desc-wrapper text-start">
            {data?.[heading.value] == null
              ? '--'
              : String(data?.[heading.value])}
          </td>
        );
    }
  };

  return (
    <>
      <div
        className={`manage-table table-responsive-wrapper main-table table-responsive  ${
          ActionData && ActionData?.length > 0 ? 'sticky-btns' : ''
        }`}
        ref={tableWrapperRef}
        tabIndex={0}
      >
        {tableView == 'grid' ? (
          <>
            {' '}
            <CardView
              loading={isLoading ?? false}
              tableData={tableData}
              tableHeading={tableHeading}
              handleStatusToggle={handleStatusToggle}
              handleViewDetail={handleViewDetail}
              ActionData={ActionData}
            />
          </>
        ) : (
          <table className="table table-border-bottom table-responsive align-middle nowrap w-100">
            <thead>
              <tr>
                {tableHeading?.map((data, index) => (
                  <th
                    key={index}
                    className={`${
                      data?.value == 'empNameWithImage'
                        ? 'fixed-column'
                        : data?.value === 'emp' && 'text-start'
                    } text-muted text-small text-uppercase  text-nowrap text-start`}
                  >
                    {data?.label}
                  </th>
                ))}
                {ActionData && ActionData?.length > 0 && (
                  <th className="text-muted text-small text-uppercase  text-nowrap text-center">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody className={isLoading ? 'loading-skeleton' : ''}>
              {isLoading ? (
                <>
                  {[1, 2, 3].map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      {tableHeading?.map((_, key) => (
                        <td key={`skeleton-cell-${key}`}>
                          <div className="">-</div>
                        </td>
                      ))}
                      {ActionData && (
                        <td key={`skeleton-action-${index}`}>
                          <div className="">-</div>
                        </td>
                      )}
                    </tr>
                  ))}
                </>
              ) : tableData && tableData?.length > 0 ? (
                tableData?.map((data, index) => {
                  return (
                    <>
                      <tr key={`row-${index}`}>
                        {onSelectionChange && (
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input mb-2 rounded-2"
                              style={{ width: '16px', height: '16px' }}
                            />
                          </td>
                        )}
                        {tableHeading?.map((heading) =>
                          renderTableData(heading, data, index),
                        )}
                        {ActionData && (
                          <td
                            data-bs-toggle="collapse"
                            data-bs-target="#row1"
                            className="cursor-pointer"
                          >
                            <div className="action-btns d-flex gap-2 justify-content-center">
                              {ActionData?.length > 0 &&
                                ActionData?.map((action, index) => {
                                  if (
                                    action.isHidden &&
                                    action.isHidden(data)
                                  ) {
                                    return null;
                                  }
                                  const showDelete =
                                    'status' in data &&
                                    action?.title == 'delete' &&
                                    data?.status != 0;
                                  // if (
                                  //   'status' in data &&
                                  //   action?.title == 'delete'
                                  // ) {
                                  //   if (data?.status == 0) {
                                  //     return (
                                  //       <Button
                                  //         key={index}
                                  //         className={'status' in data &&
                                  //           action?.title == 'delete' ? "" : action.className}
                                  //         title={action.title}
                                  //         disabled={action?.disabled}
                                  //         onClick={() => action.clickHandler(data)}
                                  //       >
                                  //         {!action?.icon && action.label}
                                  //         {action?.icon}
                                  //       </Button>
                                  //     );
                                  //   }
                                  //   return null
                                  // }
                                  return (
                                    <Button
                                      key={index}
                                      className={
                                        showDelete
                                          ? 'invisible ' + action.className
                                          : action.className
                                      }
                                      title={action.title}
                                      disabled={
                                        typeof action?.disabled === 'function'
                                          ? action.disabled(data)
                                          : action?.disabled
                                      }
                                      onClick={() => action.clickHandler(data)}
                                    >
                                      {!action?.icon && action.label}
                                      {typeof action.icon === 'function'
                                        ? (
                                            action.icon as (
                                              data: T,
                                            ) => ReactNode
                                          )(data)
                                        : action.icon}
                                    </Button>
                                  );
                                })}
                            </div>
                          </td>
                        )}
                      </tr>

                      {/* nested table  */}
                      {/* <tr className="collapse" id="row1">
                        <td colSpan={4} className="px-0">
                          <div className="manage-table table-responsive-wrapper main-table table-responsive  sticky-btns">
                            <table className="table table-border-bottom table-responsive align-middle nowrap w-100">
                              <thead>
                                <th className="false text-muted text-small text-uppercase  text-nowrap text-start">
                                  Assets
                                </th>
                                <th className="false text-muted text-small text-uppercase  text-nowrap text-start">
                                  Status
                                </th>
                                <th className="false text-muted text-small text-uppercase  text-nowrap text-start">
                                  Tracking ID
                                </th>
                                <th className="false text-muted text-small text-uppercase  text-nowrap text-start">
                                  Batch No.
                                </th>
                                <th className="false text-muted text-small text-uppercase  text-nowrap text-start">
                                  Actions
                                </th>
                              </thead>
                              <tbody>
                                <td className="text-start nested_td">
                                  <div
                                    className="user"
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <img
                                      src={'https://i.pravatar.cc/300'}
                                      alt="User"
                                      className="user-avatar me-2"
                                      style={{
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '50%',
                                      }}
                                    />

                                    <div
                                      onClick={
                                        handleViewDetail
                                          ? () => handleViewDetail(data)
                                          : () => {}
                                      }
                                    >
                                      <h4 className="title mb-0">Apple M2</h4>
                                    </div>
                                  </div>
                                </td>
                                <td className="text-start nested_td">In use</td>
                                <td className="text-start nested_td">
                                  Vikram Apple
                                </td>
                                <td className="text-start nested_td">
                                  Mac Book M1
                                </td>
                                {ActionData && (
                                  <td aria-expanded="true">
                                    <div className="action-btns d-flex gap-2 justify-content-start">
                                      {ActionData?.length > 0 &&
                                        ActionData?.map((action, index) => {
                                          const showDelete =
                                            'status' in data &&
                                            action?.title == 'delete' &&
                                            data?.status != 0;

                                          return (
                                            <Button
                                              key={index}
                                              className={
                                                showDelete
                                                  ? 'invisible ' +
                                                    action.className
                                                  : action.className
                                              }
                                              title={action.title}
                                              disabled={action?.disabled}
                                              onClick={() =>
                                                action.clickHandler(data)
                                              }
                                            >
                                              {!action?.icon && action.label}
                                              {action?.icon}
                                            </Button>
                                          );
                                        })}
                                    </div>
                                  </td>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr> */}
                    </>
                  );
                })
              ) : (
                <tr>
                  <td className="text-center border-0" colSpan={12}>
                    <div className="d-flex flex-column justify-content-center align-items-center no-data-error">
                      <NoRecordIcon />
                      {'No record found'}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {tableData?.length > 0 && (
        <div className="d-flex justify-content-between align-items-center pt-3">
          <div className="d-flex align-items-center gap-2">
            {count > 0 && !isLoading && (
              <div className="dropdown short">
                <button
                  className="btn dropdown-toggle border"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {pageSize}
                  <span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M6.40039 8.19922L10.0004 11.7992L13.6004 8.19922"
                        stroke="#36394A"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></path>
                    </svg>
                  </span>
                </button>
                <ul className="dropdown-menu ">
                  {pageSizes.map((value) => (
                    <li key={value}>
                      <button
                        className={`dropdown-item ${
                          pageSize === value && 'active'
                        }`}
                        type="button"
                        onClick={() =>
                          handleChangePageSize && handleChangePageSize(value)
                        }
                      >
                        {value}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              {count > 0 && !isLoading && (
                <p
                  className="mb-0 text-muted fw-medium text-uppercase text-nowrap"
                  style={{ fontSize: '12px' }}
                >
                  {`Showing ${currentPage * pageSize - (pageSize - 1)} to ${
                    currentPage * pageSize < count
                      ? currentPage * pageSize
                      : count
                  } of ${count} entries`}
                </p>
              )}
            </div>
          </div>
          <PaginationComponent
            currentPage={currentPage}
            pageChangeHandler={pageChangeHandler}
            totalPages={totalPages}
            pageSize={pageSize}
          />
          {selectedIds && selectedIds?.length > 0 && (
            <div className="d-flex gap-2">
              <Button
                disabled={!selectedIds || selectedIds?.length === 0}
                className="btn btn-success"
                onClick={() => handleApprove && handleApprove()}
              >
                Approve
              </Button>
              <Button
                disabled={!selectedIds || selectedIds?.length === 0}
                className="btn btn-danger"
                onClick={() => handleReject && handleReject()}
              >
                Reject
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
